import { useState } from 'react';

import {
  Alert,
  Box,
  Card,
  Chip,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';

import { EmptyState, ErrorState, LoadingState } from '@/shared/ui';

import { useProductPriceLines, useProducts, useSearchProducts, useSalesChannels } from '../../hooks';
import { formatPrice } from '../../lib/format';
import { effectiveTierAmount, resolveGridCell } from '../../lib/priceGrid';
import { useCatalogPermissions } from '../../lib/useCatalogPermissions';
import { useToast } from '../../lib/useToast';
import type { ToastSeverity } from '../../lib/useToast';
import type { PriceLineResponse, Product } from '../../model/types';
import { PriceCellEditorDrawer } from './PriceCellEditorDrawer';

// ── Constants ─────────────────────────────────────────────────────────────────

const TIERS = [1, 10, 100] as const;

// ── Helpers ───────────────────────────────────────────────────────────────────

type ChipColor = 'success' | 'warning' | 'error' | 'default';

function stateColor(state: string): ChipColor {
  if (state === 'ACTIVE') return 'success';
  if (state === 'REQUIRES_APPROVAL') return 'warning';
  if (state === 'BLOCKED') return 'error';
  return 'default';
}

// ── Sub-component: PriceGridRow ───────────────────────────────────────────────

interface PriceGridRowProps {
  product: Product;
  selectedChannel: string;
  canPricingManage: boolean;
  onCellClick: (
    product: { id: string; name?: string },
    channelCode: string | undefined,
    lines: PriceLineResponse[],
  ) => void;
}

function PriceGridRow({ product, selectedChannel, canPricingManage, onCellClick }: PriceGridRowProps) {
  const { data: lines = [] } = useProductPriceLines(product.id);
  const now = new Date();
  const channelCode = selectedChannel || null;
  const resolution = resolveGridCell(lines, channelCode, now);

  const handleCellClick = () => {
    if (canPricingManage) {
      onCellClick({ id: product.id, name: product.name }, selectedChannel || undefined, lines);
    }
  };

  return (
    <TableRow hover>
      <TableCell>
        <Typography variant="body2" fontWeight={500}>
          {product.name}
        </Typography>
      </TableCell>

      {TIERS.map((tier) => {
        const amount = resolution ? effectiveTierAmount(resolution.line, tier) : undefined;
        return (
          <TableCell
            key={tier}
            align="right"
            onClick={handleCellClick}
            role={canPricingManage ? 'button' : undefined}
            tabIndex={canPricingManage ? 0 : undefined}
            onKeyDown={
              canPricingManage
                ? (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleCellClick();
                    }
                  }
                : undefined
            }
            sx={{
              cursor: canPricingManage ? 'pointer' : 'default',
              '&:focus-visible': canPricingManage
                ? {
                    outline: '2px solid',
                    outlineColor: 'primary.main',
                    outlineOffset: '-2px',
                  }
                : undefined,
            }}
          >
            <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
              {resolution?.inherited ? (
                <Typography component="span" variant="caption" color="text.disabled">
                  = база
                </Typography>
              ) : null}
              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                {amount !== undefined ? formatPrice(amount) : '—'}
              </Typography>
            </Stack>
          </TableCell>
        );
      })}

      <TableCell>
        {resolution ? (
          <Chip size="small" label={resolution.line.state} color={stateColor(resolution.line.state)} />
        ) : (
          <Typography variant="body2" color="text.disabled">
            —
          </Typography>
        )}
      </TableCell>
    </TableRow>
  );
}

// ── Drawer state ──────────────────────────────────────────────────────────────

interface DrawerState {
  product: { id: string; name?: string };
  channelCode?: string;
  lines: PriceLineResponse[];
}

// ── Main export ───────────────────────────────────────────────────────────────

export function PriceGridPanel() {
  const [query, setQuery] = useState('');
  const [selectedChannel, setSelectedChannel] = useState('');
  const [drawerState, setDrawerState] = useState<DrawerState | null>(null);

  const { canPricingView, canPricingManage } = useCatalogPermissions();
  const { toast, notify, clear } = useToast();

  const {
    data: channelsResult,
    isLoading: channelsLoading,
    isError: channelsError,
    refetch: refetchChannels,
  } = useSalesChannels();

  const listQ = useProducts({}, { enabled: !query });
  const searchQ = useSearchProducts({ query }, { enabled: Boolean(query) });

  const productsLoading = query ? searchQ.isLoading : listQ.isLoading;
  const productsError = query ? searchQ.isError : listQ.isError;

  // ── Permission guard ──────────────────────────────────────────────────────

  if (!canPricingView) {
    return (
      <ErrorState
        title="Доступ ограничен"
        message="У вас нет прав на управление ценами. Обратитесь к администратору компании."
      />
    );
  }

  // ── Async guards ──────────────────────────────────────────────────────────

  if (channelsLoading || productsLoading) {
    return <LoadingState label="Загрузка прайс-сетки…" />;
  }

  if (channelsError || productsError) {
    return (
      <ErrorState
        message="Не удалось загрузить данные"
        onRetry={() => {
          void refetchChannels();
          void (query ? searchQ.refetch() : listQ.refetch());
        }}
      />
    );
  }

  // ── Data ──────────────────────────────────────────────────────────────────

  const channels = channelsResult ?? [];
  const products = (query ? searchQ.data?.content : listQ.data?.content) ?? [];

  // ── Handlers ─────────────────────────────────────────────────────────────

  const handleCellClick = (
    product: { id: string; name?: string },
    channelCode: string | undefined,
    lines: PriceLineResponse[],
  ) => {
    setDrawerState({ product, channelCode, lines });
  };

  const handleDrawerResult = (severity: ToastSeverity, message: string): void => {
    notify(severity, message);
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Box>
      {/* Toolbar */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        sx={{ mb: 2 }}
      >
        <TextField
          size="small"
          label="Поиск товара"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          sx={{ minWidth: 240 }}
        />
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="body2" color="text.secondary">
            Канал:
          </Typography>
          <ToggleButtonGroup
            value={selectedChannel}
            exclusive
            size="small"
            onChange={(_e, newValue: string | null) => {
              if (newValue !== null) setSelectedChannel(newValue);
            }}
          >
            <ToggleButton value="">База</ToggleButton>
            {channels
              .filter((c) => c.active)
              .map((c) => (
                <ToggleButton key={c.code} value={c.code}>
                  {c.name}
                </ToggleButton>
              ))}
          </ToggleButtonGroup>
        </Stack>
      </Stack>

      {/* Table or empty */}
      {products.length === 0 ? (
        <EmptyState
          title="Товары не найдены"
          description="Попробуйте изменить параметры поиска."
        />
      ) : (
        <Card variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Товар</TableCell>
                {TIERS.map((t) => (
                  <TableCell key={t} align="right">
                    от {t}
                  </TableCell>
                ))}
                <TableCell>Статус</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {products.map((product) => (
                <PriceGridRow
                  key={product.id}
                  product={product}
                  selectedChannel={selectedChannel}
                  canPricingManage={canPricingManage}
                  onCellClick={handleCellClick}
                />
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Editor drawer */}
      <PriceCellEditorDrawer
        open={drawerState !== null}
        product={drawerState?.product ?? { id: '' }}
        channelCode={drawerState?.channelCode}
        existingLines={drawerState?.lines ?? []}
        onClose={() => setDrawerState(null)}
        onResult={handleDrawerResult}
      />

      {/* Toast */}
      <Snackbar
        open={toast !== null}
        autoHideDuration={5000}
        onClose={clear}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={clear}
          severity={toast?.severity ?? 'info'}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {toast?.message ?? ''}
        </Alert>
      </Snackbar>
    </Box>
  );
}
